import { Grid, Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { ChildTileData, CMSViewMode } from '../../types/CMSTypes';
import { getCategoryDetails, buildCategoryUrl } from '../../api/cmsApi';

interface CMSConsumerNewArrivalsGridProps {
  newArrivalsData: ChildTileData[];
  header?: string;
  hasTitles?: boolean;
  hasButtons?: boolean;
  hasBackgroundText?: boolean;
  spacingValue?: number;
  marginTop?: string | number;
  marginBottom?: string | number;
  viewMode?: CMSViewMode;
}

const CMSConsumerNewArrivalsGrid: React.FC<CMSConsumerNewArrivalsGridProps> = ({
                                                                                 newArrivalsData,
                                                                                 header = "New arrivals",
                                                                                 hasTitles = true,
                                                                                 hasButtons = true,
                                                                                 hasBackgroundText = false,
                                                                                 spacingValue = 4,
                                                                                 marginTop = '5rem',
                                                                                 marginBottom = '5rem',
                                                                                 viewMode = 'live'
                                                                               }) => {
  const navigate = useNavigate();

  // Handle navigation for collection items
  const handleExploreClick = async (item: ChildTileData) => {
    try {
      // Build the collection URL using the same logic as carousel banner
      const categoryResponse = await getCategoryDetails(item.category_id, viewMode);
      if (categoryResponse.success && categoryResponse.cms_category) {
        const categoryUrl = buildCategoryUrl(categoryResponse.cms_category);
        navigate(categoryUrl);
      } else {
        // Fallback to manual URL building
        if (item.item_group && item.application && item.collection) {
          const url = `/item_group/${item.item_group}/application/${encodeURIComponent(item.application)}/collection/${encodeURIComponent(item.collection)}`;
          navigate(url);
        }
      }
    } catch (error) {
      console.error('Error navigating to collection:', error);
      // Final fallback - use the application/collection structure
      if (item.item_group && item.application && item.collection) {
        const url = `/item_group/${item.item_group}/application/${encodeURIComponent(item.application)}/collection/${encodeURIComponent(item.collection)}`;
        navigate(url);
      }
    }
  };

  // Show missing content if no data
  if (!newArrivalsData || newArrivalsData.length === 0) {
    return <MissingContent sectionName="New Arrivals" />;
  }

  return (
    <Box sx={{ mt: marginTop, mb: marginBottom }}>
      {/* Header */}
      <Box
        sx={{
          ml: { xs: '0', lg: '4rem' },
          mb: -2,
          pl: { xs: '5%', lg: '0' },
        }}
      >
        <Typography
          fontWeight="300"
          variant="h3"
          sx={{
            fontSize: { xs: '44px', lg: '56px' },
          }}
        >
          {header}
        </Typography>
      </Box>

      {/* Grid */}
      <Grid container spacing={'16px'} sx={{ marginTop: '30px' }}>
        {newArrivalsData.map((item, index) => (
          <Grid key={item.category_id || index} item xs={12} sm={6} md={spacingValue}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: {
                  xs: 'auto',
                  md: '650px',
                },
              }}
            >
              {/* Image */}
              <img
                src={item.tile_display_media_single_link || '/placeholder-image.jpg'}
                style={{
                  width: '100%',
                  objectFit: 'cover',
                  height: '100%',
                }}
                alt={item.collection || item.category_name || 'New Arrival'}
              />

              {/* Background text overlay */}
              {hasBackgroundText && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: 'center',
                      color: '#fff',
                      fontSize: '2rem',
                      textTransform: 'uppercase',
                    }}
                  >
                    {item.collection || item.category_name}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Title and Button */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '1.5rem',
              }}
            >
              {hasTitles && (
                <Typography
                  variant="h5"
                  fontSize={'40px'}
                  fontWeight={'400'}
                >
                  {item.collection || item.category_name}
                </Typography>
              )}

              {hasButtons && (
                <Button
                  sx={{
                    margin: '1rem 0rem',
                    borderRadius: '1px',
                    padding: { xs: '1rem 3rem', lg: '24px' },
                    width: { xs: 'auto', lg: '280px' },
                    border: { xs: '', lg: '1px solid #000' },
                  }}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleExploreClick(item)}
                >
                  Explore More
                </Button>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CMSConsumerNewArrivalsGrid;